import { Box, Tab, Tabs } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import UpcomingSessionTable from './UpcomingSessionTable/UpcomingSessionTable';
import CompletedSessionTable from './CompletedSessionTable/CompletedSessionTable';
import CancelledSessionTable from './CancelledSessionTable/CancelledSessionTable';
import { useParams } from 'react-router-dom';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import { TableSkeleton } from '../../../../../components/Admin/SkeletonLoader/SkeletonLoader';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      aria-label={`${props.label} panel`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const TeacherSession = () => {
  const { id } = useParams();

  // Get initial session tab value from session storage or default to 0
  const getInitialSessionTabValue = () => {
    const savedTab = sessionStorage.getItem(`teacherSessionTab-${id}`);
    if (savedTab !== null) {
      const tabValue = parseInt(savedTab, 10);
      return isNaN(tabValue) ? 0 : tabValue;
    }
    return 0;
  };

  const [value, setValue] = useState(getInitialSessionTabValue());
  const [upcomingSessionData, setUpcomingSessionData] = useState([]);
  const [completedSessionData, setCompletedSessionData] = useState([]);
  const [cancelledSessionData, setCancelledSessionData] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  const [loadingCancelled, setLoadingCancelled] = useState(false);
  const [hasLoadedUpcoming, setHasLoadedUpcoming] = useState(false);
  const [hasLoadedCompleted, setHasLoadedCompleted] = useState(false);
  const [hasLoadedCancelled, setHasLoadedCancelled] = useState(false);

  const getUpcomingSessionData = useCallback(async () => {
    try {
      setLoadingUpcoming(true);
      const { data } = await axiosInstance.get(`/admin/sessions/${id}?status=upcoming`);
      setUpcomingSessionData(data.results || []);
      setHasLoadedUpcoming(true);
    } catch (error) {
      console.log(error);
      setUpcomingSessionData([]);
      setHasLoadedUpcoming(true);
    } finally {
      setLoadingUpcoming(false);
    }
  }, [id]);

  const getCompletedSessionData = useCallback(async () => {
    try {
      setLoadingCompleted(true);
      const { data } = await axiosInstance.get(`/admin/sessions/${id}?status=completed`);
      setCompletedSessionData(data.results || []);
      setHasLoadedCompleted(true);
    } catch (error) {
      console.log(error);
      setCompletedSessionData([]);
      setHasLoadedCompleted(true);
    } finally {
      setLoadingCompleted(false);
    }
  }, [id]);

  const getCancelledSessionData = useCallback(async () => {
    try {
      setLoadingCancelled(true);
      const { data } = await axiosInstance.get(`/admin/sessions/${id}?status=cancelled`);
      setCancelledSessionData(data.results || []);
      setHasLoadedCancelled(true);
    } catch (error) {
      console.log(error);
      setCancelledSessionData([]);
      setHasLoadedCancelled(true);
    } finally {
      setLoadingCancelled(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      getUpcomingSessionData();
    }
  }, [id, getUpcomingSessionData]);

  useEffect(() => {
    if (value === 0 && !hasLoadedUpcoming) {
      getUpcomingSessionData();
    } else if (value === 1 && !hasLoadedCompleted) {
      getCompletedSessionData();
    } else if (value === 2 && !hasLoadedCancelled) {
      getCancelledSessionData();
    }
  }, [
    value,
    hasLoadedUpcoming,
    hasLoadedCompleted,
    hasLoadedCancelled,
    getUpcomingSessionData,
    getCompletedSessionData,
    getCancelledSessionData,
  ]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    // Save session tab selection to session storage
    sessionStorage.setItem(`teacherSessionTab-${id}`, newValue.toString());
  };

  return (
    <Box
      data-testid="teacher-session-component"
      className="h-[calc(100vh-260px)] overflow-auto relative bg-[#F9F9F9] rounded-lg"
    >
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
            <Tab label="Upcoming" sx={{ textTransform: 'none' }} {...a11yProps(0)} />
            <Tab label="Completed" sx={{ textTransform: 'none' }} {...a11yProps(1)} />
            <Tab label="Cancelled" sx={{ textTransform: 'none' }} {...a11yProps(2)} />
          </Tabs>
        </Box>
        <CustomTabPanel value={value} index={0}>
          {loadingUpcoming ? (
            <TableSkeleton />
          ) : (
            <UpcomingSessionTable upcomingSessionData={upcomingSessionData} />
          )}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          {loadingCompleted ? (
            <TableSkeleton />
          ) : (
            <CompletedSessionTable completedSessionData={completedSessionData} />
          )}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          {loadingCancelled ? (
            <TableSkeleton />
          ) : (
            <CancelledSessionTable cancelledSessionData={cancelledSessionData} />
          )}
        </CustomTabPanel>
      </Box>
    </Box>
  );
};

export default TeacherSession;
